import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContextService } from '@maximizer/core/shared/data-access';
import {
  insightsHeader,
  InteractionLogFieldOption,
  InteractionLogForm,
  Octopus,
} from '@maximizer/core/shared/domain';
import {
  InteractionFieldOptionMapper,
  InteractionFormMapper,
} from '@maximizer/outlook/shared/domain';
import { map, Observable } from 'rxjs';

@Injectable()
export class InteractionService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  getFieldOption(): Observable<InteractionLogFieldOption> {
    const request: Octopus.InteractionLogFieldOptionsRequest = {
      InteractionLog: {
        FieldOptions: {
          Category: {
            Scope: {
              Fields: {
                Key: 1,
                DisplayValue: 1,
              },
            },
            Criteria: {
              SearchQuery: {
                InActive: {
                  $EQ: false,
                },
              },
            },
          },
          Result: {
            Scope: {
              Fields: {
                Key: 1,
                DisplayValue: 1,
              },
            },
            Criteria: {
              SearchQuery: {
                InActive: {
                  $EQ: false,
                },
              },
            },
          },
        },
      },
    };

    return this.http
      .post<Octopus.InteractionLogFieldOptionsResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new InteractionFieldOptionMapper();
            return mapper.from(result);
          }
          return { categories: [], results: [] };
        }),
      );
  }
  save(interactionForm: InteractionLogForm): Observable<string | null> {
    const mapper = new InteractionFormMapper();
    const data = mapper.from(interactionForm);
    const type = this.getTextFromKey(interactionForm.type);
    const request: Octopus.InteractionLogFormRequest = {
      InteractionLog: {
        Data: data,
      },
      Globalization: Octopus.DefaultGlobalization,
    };
    return this.http
      .post<Octopus.InteractionLogFormResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
        {
          headers: {
            [insightsHeader('interaction-type')]: type ?? interactionForm.type,
          },
        },
      )
      .pipe(
        map((result) => {
          if (result?.Code === Octopus.ResponseStatusCode.Successful) {
            return result.InteractionLog.Data.Key;
          }
          return null;
        }),
      );
  }

  getLastContact(leadKey: string): Observable<Date | null> {
    const query: Octopus.LogicalQuery<Octopus.Task> = {
      $AND: [
        { StartDate: { $LE: new Date().toISOString() } },
        { LeadKey: { $EQ: leadKey } },
      ],
    };
    const request: Octopus.InteractionLogReadRequest = {
      InteractionLog: {
        Criteria: {
          SearchQuery: query,
          Top: 1,
        },
        Scope: { Fields: { Key: 1, StartDate: 1 } },
        OrderBy: { Fields: [{ StartDate: 'DESC' }] },
      },
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.InteractionLogReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result?.Code === Octopus.ResponseStatusCode.Successful) {
            const data = result.InteractionLog.Data;
            if (data.length > 0 && data[0].StartDate) {
              // The wire value is an ISO string despite the model's Date type
              return new Date(data[0].StartDate);
            }
          }
          return null;
        }),
      );
  }

  private getTextFromKey(code: string): string | undefined {
    const enumValue = Object.values(Octopus.InteractionCode).find(
      (interactionCode) => interactionCode === code,
    ) as Octopus.InteractionCode;
    if (enumValue == undefined) return enumValue;

    return Octopus.InteractionCode[enumValue];
  }
}
